<script setup>
import { ref, computed, watch } from 'vue';
import { useMFA } from '@/composables/useMFA';

const emit = defineEmits(['enrolled', 'cancel']);

const mfa = useMFA();

const step = ref(1); // 1: QR Code, 2: Verify, 3: Backup Codes
const isLoading = ref(false);
const errorMessage = ref('');

// TOTP setup data
const totpData = ref({
  secret: '',
  qr_code: '',
  provisioning_uri: '',
});

// Verification
const verifyCode = ref(['', '', '', '', '', '']);
const verifyInputs = ref([]);
const backupCodes = ref([]);

const fullVerifyCode = computed(() => verifyCode.value.join(''));

// Start TOTP setup
const startSetup = async () => {
  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await mfa.setupTOTP();
    
    // Handle different possible API response formats
    let qrCode = response.qr_code || response.qrCode || response.qr || '';
    
    // Add data URI prefix if missing
    if (qrCode && !qrCode.startsWith('data:')) {
      qrCode = `data:image/png;base64,${qrCode}`;
    }
    
    totpData.value = {
      secret: response.secret || response.totp_secret || '',
      qr_code: qrCode,
      provisioning_uri: response.provisioning_uri || response.provisioningUri || response.uri || '',
    };
  } catch (err) {
    errorMessage.value = err.message || 'Failed to start TOTP setup';
  } finally {
    isLoading.value = false;
  }
};

// Handle OTP input
const handleVerifyInput = (index, event) => {
  const value = event.target.value;

  if (!/^\d*$/.test(value)) {
    verifyCode.value[index] = '';
    return;
  }

  if (value.length > 1) {
    const digits = value.split('').filter((d) => /\d/.test(d)).slice(0, 6);
    digits.forEach((digit, i) => {
      if (i < 6) verifyCode.value[i] = digit;
    });
    const lastIndex = Math.min(digits.length - 1, 5);
    verifyInputs.value[lastIndex]?.focus();
    return;
  }

  verifyCode.value[index] = value;

  if (value && index < 5) {
    verifyInputs.value[index + 1]?.focus();
  }
};

const handleVerifyKeydown = (index, event) => {
  if (event.key === 'Backspace' && !verifyCode.value[index] && index > 0) {
    verifyInputs.value[index - 1]?.focus();
  }
};

// Auto-verify when 6 digits entered
watch(fullVerifyCode, (code) => {
  if (code.length === 6) {
    confirmSetup();
  }
});

// Confirm TOTP setup
const confirmSetup = async () => {
  if (fullVerifyCode.value.length !== 6) {
    errorMessage.value = 'Please enter a 6-digit code';
    return;
  }

  isLoading.value = true;
  errorMessage.value = '';

  try {
    const response = await mfa.confirmTOTP(fullVerifyCode.value);
    backupCodes.value = response.backup_codes || [];
    step.value = 3;
  } catch (err) {
    errorMessage.value = err.message || 'Invalid code. Please try again.';
    verifyCode.value = ['', '', '', '', '', ''];
    verifyInputs.value[0]?.focus();
  } finally {
    isLoading.value = false;
  }
};

// Copy secret to clipboard
const copySecret = async () => {
  try {
    await navigator.clipboard.writeText(totpData.value.secret);
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = totpData.value.secret;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

// Copy backup codes
const copyBackupCodes = async () => {
  const codesText = backupCodes.value.join('\n');
  try {
    await navigator.clipboard.writeText(codesText);
  } catch {
    const textArea = document.createElement('textarea');
    textArea.value = codesText;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

// Download backup codes
const downloadBackupCodes = () => {
  const codesText = `Calid Backup Codes\n==================\n\nKeep these codes safe. Each code can only be used once.\n\n${backupCodes.value.join('\n')}\n\nGenerated: ${new Date().toISOString()}`;
  const blob = new Blob([codesText], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'calid-backup-codes.txt';
  a.click();
  URL.revokeObjectURL(url);
};

const finishSetup = () => {
  emit('enrolled');
};

// Start setup on mount
startSetup();
</script>

<template>
  <div class="totp-enrollment">
    <!-- Step 1: QR Code -->
    <div v-if="step === 1" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon">
          <i class="fas fa-mobile-alt"></i>
        </div>
        <h3>Set Up Authenticator App</h3>
        <p>Scan the QR code with your authenticator app (Google Authenticator, Authy, etc.)</p>
      </div>

      <div v-if="isLoading" class="loading-state">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
        <p>Generating QR code...</p>
      </div>

      <div v-else-if="totpData.qr_code" class="qr-container">
        <div class="qr-code">
          <img :src="totpData.qr_code" alt="TOTP QR Code" />
        </div>

        <div class="manual-entry">
          <p class="manual-label">Can't scan? Enter this code manually:</p>
          <div class="secret-display">
            <code>{{ totpData.secret }}</code>
            <button class="copy-btn" @click="copySecret" title="Copy to clipboard">
              <i class="fas fa-copy"></i>
            </button>
          </div>
        </div>
      </div>

      <div v-if="errorMessage" class="error-message">
        <i class="fas fa-exclamation-circle"></i>
        {{ errorMessage }}
      </div>

      <div class="step-actions">
        <button class="btn-secondary" @click="emit('cancel')">Cancel</button>
        <button class="btn-primary" @click="step = 2" :disabled="!totpData.secret">
          Next
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>

    <!-- Step 2: Verify Code -->
    <div v-else-if="step === 2" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon step-icon-verify">
          <i class="fas fa-check-circle"></i>
        </div>
        <h3>Verify Setup</h3>
        <p>Enter the 6-digit code from your authenticator app to confirm setup</p>
      </div>

      <div class="verify-container">
        <div class="otp-inputs">
          <input
            v-for="(digit, index) in verifyCode"
            :key="index"
            :ref="(el) => (verifyInputs[index] = el)"
            type="text"
            inputmode="numeric"
            maxlength="6"
            class="otp-input"
            :class="{ 'has-value': digit }"
            :value="digit"
            @input="handleVerifyInput(index, $event)"
            @keydown="handleVerifyKeydown(index, $event)"
            :disabled="isLoading"
          />
        </div>

        <div v-if="errorMessage" class="error-message">
          <i class="fas fa-exclamation-circle"></i>
          {{ errorMessage }}
        </div>
      </div>

      <div class="step-actions">
        <button class="btn-secondary" @click="step = 1">
          <i class="fas fa-arrow-left"></i>
          Back
        </button>
        <button
          class="btn-primary"
          @click="confirmSetup"
          :disabled="fullVerifyCode.length !== 6 || isLoading"
        >
          <span v-if="isLoading" class="spinner-border spinner-border-sm me-2"></span>
          {{ isLoading ? 'Verifying...' : 'Verify & Enable' }}
        </button>
      </div>
    </div>

    <!-- Step 3: Backup Codes -->
    <div v-else-if="step === 3" class="enrollment-step">
      <div class="step-header">
        <div class="step-icon step-icon-success">
          <i class="fas fa-shield-alt"></i>
        </div>
        <h3>Save Your Backup Codes</h3>
        <p>Keep these codes safe! You can use them to access your account if you lose your authenticator.</p>
      </div>

      <div class="backup-codes-container">
        <div class="backup-codes-grid">
          <div v-for="(code, index) in backupCodes" :key="index" class="backup-code">
            {{ code }}
          </div>
        </div>

        <div class="backup-actions">
          <button class="backup-btn" @click="copyBackupCodes">
            <i class="fas fa-copy"></i>
            Copy Codes
          </button>
          <button class="backup-btn" @click="downloadBackupCodes">
            <i class="fas fa-download"></i>
            Download
          </button>
        </div>

        <div class="backup-warning">
          <i class="fas fa-exclamation-triangle"></i>
          <span>Each code can only be used once. Store them securely.</span>
        </div>
      </div>

      <div class="step-actions">
        <button class="btn-primary btn-full" @click="finishSetup">
          <i class="fas fa-check"></i>
          I've Saved My Codes
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.totp-enrollment {
  width: 100%;
  max-width: 480px;
}

.enrollment-step {
  background: var(--calid-card-bg, #111827);
  border-radius: 16px;
  padding: 2rem;
}

.step-header {
  text-align: center;
  margin-bottom: 2rem;
}

.step-icon {
  width: 72px;
  height: 72px;
  margin: 0 auto 1rem;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-icon i {
  font-size: 32px;
  color: white;
}

.step-icon-verify {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
}

.step-icon-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
}

.step-header h3 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--calid-text-primary, #f0f0f5);
  margin-bottom: 0.5rem;
}

.step-header p {
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.95rem;
  margin: 0;
}

.loading-state {
  text-align: center;
  padding: 2rem;
}

.loading-state p {
  margin-top: 1rem;
  color: var(--calid-text-secondary, #94a3b8);
}

.qr-container {
  text-align: center;
}

.qr-code {
  background: white;
  padding: 1rem;
  border-radius: 12px;
  display: inline-block;
  margin-bottom: 1.5rem;
}

.qr-code img {
  width: 200px;
  height: 200px;
}

.manual-entry {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  border-radius: 12px;
  padding: 1rem;
}

.manual-label {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.75rem;
}

.secret-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.secret-display code {
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.85rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-primary, #f0f0f5);
  padding: 0.5rem 1rem;
  border-radius: 6px;
  letter-spacing: 1px;
}

.copy-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  cursor: pointer;
  color: var(--calid-text-secondary, #94a3b8);
  transition: all 0.2s;
}

.copy-btn:hover {
  background: rgba(0, 212, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.3);
  color: #00d4ff;
}

.verify-container {
  margin-bottom: 1.5rem;
}

.otp-inputs {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.otp-input {
  width: 52px;
  height: 60px;
  border: 2px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 12px;
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-primary, #f0f0f5);
  transition: all 0.2s ease;
  outline: none;
}

.otp-input:focus {
  border-color: #00d4ff;
  background: var(--calid-card-bg, #111827);
  box-shadow: 0 0 0 3px rgba(0, 212, 255, 0.15);
}

.otp-input.has-value {
  border-color: #00d4ff;
  background: var(--calid-card-bg, #111827);
}

.error-message {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 1rem;
}

.backup-codes-container {
  margin-bottom: 1.5rem;
}

.backup-codes-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.backup-code {
  font-family: 'JetBrains Mono', 'SF Mono', monospace;
  font-size: 0.95rem;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.08));
  color: var(--calid-text-primary, #f0f0f5);
  padding: 0.75rem;
  border-radius: 8px;
  text-align: center;
  letter-spacing: 1px;
}

.backup-actions {
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-bottom: 1rem;
}

.backup-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  border-radius: 8px;
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.03));
  color: var(--calid-text-secondary, #94a3b8);
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s;
}

.backup-btn:hover {
  border-color: rgba(0, 212, 255, 0.3);
  color: #00d4ff;
  background: rgba(0, 212, 255, 0.05);
}

.backup-warning {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  border-radius: 8px;
  font-size: 0.85rem;
}

.step-actions {
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-top: 1.5rem;
}

.btn-primary,
.btn-secondary {
  padding: 0.875rem 1.5rem;
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary {
  background: #00d4ff;
  color: #0a0a0f;
  font-weight: 600;
  flex: 1;
}

.btn-primary:hover:not(:disabled) {
  background: #00b8e0;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--calid-bg-tertiary, rgba(255, 255, 255, 0.05));
  border: 1px solid var(--calid-border, rgba(255, 255, 255, 0.1));
  color: var(--calid-text-secondary, #94a3b8);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.1);
}

.btn-full {
  width: 100%;
}

/* Light mode overrides */
:root:not(.dark-version) .enrollment-step {
  background: white;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

:root:not(.dark-version) .step-header h3 {
  color: #1f2937;
}

:root:not(.dark-version) .step-header p {
  color: #6b7280;
}

:root:not(.dark-version) .manual-entry {
  background: #f9fafb;
  border-color: #e5e7eb;
}

:root:not(.dark-version) .secret-display code {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #1f2937;
}

:root:not(.dark-version) .copy-btn {
  background: #e5e7eb;
  border-color: #d1d5db;
  color: #6b7280;
}

:root:not(.dark-version) .otp-input {
  background: #f9fafb;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root:not(.dark-version) .otp-input:focus,
:root:not(.dark-version) .otp-input.has-value {
  background: white;
}

:root:not(.dark-version) .backup-code {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #1f2937;
}

:root:not(.dark-version) .backup-btn {
  background: white;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .btn-secondary {
  background: #f3f4f6;
  border-color: #e5e7eb;
  color: #374151;
}

:root:not(.dark-version) .btn-secondary:hover {
  background: #e5e7eb;
}
</style>
